import spartaEventsUtility from "utilities/global/sparta-events-utility/2.4.1/js/sparta-events-utility";
import spartaGlobalFunctions from "utilities/global/sparta-global-functions-utility/2.2.1/js/sparta-global-functions-utility";
import _ from "utilities/vendor/lodash/4.17.21/js/lodash.custom.min";
/* eslint-disable no-underscore-dangle */

/**
 * Determines the current environment settings.
 * @returns {object} The settings for `environment` (string) and `isLLE` (boolean).
 */
function _getEnv() {
  const url = window.location.hostname;
  let result = { environment: 'PROD', isLLE: false };

  const cases = {
    cert: {
      environment: 'CERT',
      isLLE: true,
    },
    sit: {
      environment: 'SIT',
      isLLE: true,
    },
    dev: {
      environment: 'DEV',
      isLLE: true,
    },
    '127.0.0.1': {
      environment: 'LOCAL',
      isLLE: true,
    },
    localhost: {
      environment: 'LOCAL',
      isLLE: true,
    },
  };

  if (cases[url]) {
    result = cases[url];
  }

  if (window.location.protocol === 'file:') {
    result = cases.localhost;
  }

  return result;
}

const _store = {};

const EVENTS = {
  ADD_PERSISTED: 'ADD_PERSISTED.spartaStoreUtility',
  REMOVE_PERSISTED: 'REMOVE_PERSISTED.spartaStoreUtility',
  UPDATE_PERSISTED: 'UPDATE_PERSISTED.spartaStoreUtility',
};

/**
 * The global data store utility for holding page-level module and event information.
 */
class SpartaStoreUtility {
  /**
   * Initialzes a new instance of the Store Utility with global data defaults.
   */
  constructor() {
    this._key = 'sparta';
    window.sparta.store = window.sparta.store || {};

    _store.browser = {};
    _store.currentMQ = {
      orientation: null,
      resolution: null,
      viewport: null,
    };

    const lang = document.querySelector('html').getAttribute('lang');
    // const htmlLang = lang ? lang.toLowerCase().split("-")[0] : 'en';

    _store.environment = _getEnv().environment;
    _store.isLLE = _getEnv().isLLE;
    _store.lang = lang ? lang.toLowerCase() : 'en-us';
    [_store.jcrLang] = _store.lang.split('-');
    _store.state = this._getState();
    _store.useLocalStorage = this.checkStorage('localStorage');
    _store.useSessionStorage = this.checkStorage('sessionStorage');
    // _store.bankHolidays = global.spaParams.global[htmlLang].bankHolidays;
  }

  /**
   * @returns {object} The utility's event identifiers.
   */
  get EVENTS() {
    return EVENTS;
  }

  /**
   * Determines whether the data store is working properly.
   * @param {string} storageType - Should be "localStorage" or "sessionStorage".
   * @returns {boolean} Whether the test value could be saved and read back from the store.
   */
  checkStorage(storageType) {
    let checkStorage = false;

    try {
      const uid = new Date();
      const storage = window[storageType];

      storage.setItem(uid, uid);
      checkStorage = (storage.getItem(uid) === uid.toString());
      storage.removeItem(uid);
    } catch (exception) {
      // eslint-disable-next-line no-console
      console.warn(`storeUtility: ${storageType} access is unavailable.`);
    }

    return checkStorage;
  }

  /**
   * Persists a given piece of data in the store.
   * @param {string} key - The key name in the store. Can be a path (e.g. "currentMQ.viewport").
   * @param {*} [value] - A piece of data to keep in the store.
   * @param {string} [storageType] - Should be "localStorage" or "sessionStorage".
   * @returns {boolean} Whether the data was added using `addData` or not.
   */
  persist(key, value = {}, storageType = 'localStorage') {
    if (
      !_store.useLocalStorage
      || !_store.useSessionStorage
      || storageType.toLowerCase() === 'object'
    ) {
      return this.addData(key, value, false);
    }

    const storage = window[storageType];
    const storageData = storage.getItem(this._key);
    const data = (storageData === null) ? {} : JSON.parse(storageData);
    data[key] = value;
    storage.setItem(this._key, JSON.stringify(data));

    spartaEventsUtility.pub(this.EVENTS.ADD_PERSISTED, { key, value });

    return false;
  }

  /**
   * Retrieves a persisted piece of data from the store.
   * @param {string} [key] - The key name in the store. Can be a path (e.g. "currentMQ.viewport").
   * @param {string} [storageType] - Should be "localStorage" or "sessionStorage".
   * @returns {*} The value stored under the specified key.
   */
  getPersisted(key = null, storageType = 'localStorage') {
    if (
      !_store.useLocalStorage
      || !_store.useSessionStorage
      || storageType.toLowerCase() === 'object'
    ) {
      return this.getData(key);
    }

    const storage = window[storageType];
    const storageData = storage.getItem(this._key);
    const data = (storageData === null) ? {} : JSON.parse(storageData);

    return data[key];
  }

  /**
   * Removes a persisted piece of data from the store.
   * @param {string} [key] - The key name in the store. Can be a path (e.g. "currentMQ.viewport").
   * @param {string} [storageType] - Should be "localStorage" or "sessionStorage".
   * @returns {boolean} Whether the data was successfully removed or not.
   */
  removePersisted(key = null, storageType = 'localStorage') {
    if (
      !_store.useLocalStorage
      || !_store.useSessionStorage
      || storageType.toLowerCase() === 'object'
    ) {
      return this.removeData(key, false);
    }

    const storage = window[storageType];
    const storageData = storage.getItem(this._key);
    const data = (storageData === null) ? {} : JSON.parse(storageData);
    let isRemoved = false;
    let removedItem;

    if (!key) {
      storage.removeItem(this._key);
      isRemoved = true;
      removedItem = storageData;
    } else if (Object.prototype.hasOwnProperty.call(data, key)) {
      removedItem = data[key];
      delete data[key];
      storage.setItem(this._key, JSON.stringify(data));
      isRemoved = true;
    }

    spartaEventsUtility.pub(
      this.EVENTS.REMOVE_PERSISTED,
      Object.assign({ key, isRemoved }, isRemoved && { removedItem }),
    );

    return isRemoved;
  }

  /**
   * Retrieves and removes a persisted piece of data from the store.
   * @param {string} [key] - The key name in the store. Can be a path (e.g. "currentMQ.viewport").
   * @param {string} [storageType] - Should be "localStorage" or "sessionStorage".
   * @returns {*} The value stored under the specified key.
   */
  getAndRemovePersisted(key = null, storageType = 'localStorage') {
    const data = this.getPersisted(key, storageType);
    this.removePersisted(key, storageType);
    return data;
  }

  /**
   * Updates a persisted piece of data in the store.
   * @param {string} key - The key name in the store. Can be a path (e.g. "currentMQ.viewport").
   * @param {*} [newValue] - A piece of data to keep in the store.
   * @param {object} [storageType] - Should be "localStorage" or "sessionStorage".
   * @returns {boolean} Whether the data was successfully updated or not.
   */
  updatePersisted(key, newValue = {}, storageType = 'localStorage') {
    if (
      !_store.useLocalStorage
      || !_store.useSessionStorage
      || storageType.toLowerCase() === 'object'
    ) {
      return this.updateData(key, newValue, false);
    }

    const storage = window[storageType];
    const storageData = storage.getItem(this._key);
    const data = (storageData === null) ? {} : JSON.parse(storageData);
    let isUpdated = false;
    let oldValue;

    if (Object.prototype.hasOwnProperty.call(data, key)) {
      oldValue = data[key];
      data[key] = newValue;
      storage.setItem(this._key, JSON.stringify(data));
      isUpdated = true;
    }

    spartaEventsUtility.pub(
      this.EVENTS.UPDATE_PERSISTED,
      Object.assign({ key, newValue, isUpdated }, isUpdated && { oldValue }),
    );
    return isUpdated;
  }

  /**
   * Adds data to the store.
   * @param {string} key - The key name in the store. Can be a path (e.g. "currentMQ.viewport").
   * @param {*} val - A piece of data to keep in the store.
   * @param {boolean} [pubEvent] - Should this publish an event?
   * @returns {boolean} Whether the data was successfully added or not.
   */
  addData(key, val, pubEvent = true) {
    const existing = _.get(_store, key, null);
    let added = false;

    if (existing !== null) {
      this.updateData(key, val);
    } else {
      _.set(_store, key, val);

      // state values persist e.g. NC
      if (key === 'state') {
        this._setState(val);
      }

      added = true;
      if (pubEvent) {
        spartaEventsUtility.pub(
          `${key}-spartaStore`,
          {
            action: 'add',
            data: _.get(_store, key, null),
          },
        );
      }
    }

    return added;
  }

  /**
   * Updates data in the store.
   * @param {string} key - The key name in the store. Can be a path (e.g. "currentMQ.viewport").
   * @param {*} val - A piece of data to keep in the store.
   * @param {boolean} [pubEvent] - Should this publish an event?
   * @returns {boolean} Whether the data was successfully updated or not.
   */
  updateData(key, val, pubEvent = true) {
    let updated = false;

    _.set(_store, key, val);

    // state values persist e.g. NC
    if (key === 'state') {
      this._setState(val);
    }

    updated = true;

    if (pubEvent) {
      spartaEventsUtility.pub(
        `${key}-spartaStore`,
        {
          action: 'update',
          data: _.get(_store, key, null),
        },
      );
    }

    return updated;
  }

  /**
   * Retrieves data from the store.
   * @param {string} key - The key name in the store. Can be a path (e.g. "currentMQ.viewport").
   * @returns {*} The value stored under the specified key.
   */
  getData(key) {
    let data = _.get(_store, key, null);
    if (!key) {
      data = _store;
    }

    return data;
  }

  /**
   * Removes data from the store.
   * @param {string} key - The key name in the store. Can be a path (e.g. "currentMQ.viewport").
   * @param {boolean} [pubEvent] - Should this publish an event?
   * @returns {boolean} Whether the data was successfully removed or not.
   */
  removeData(key, pubEvent = true) {
    const removedItem = _.get(_store, key, null);
    const removed = _.unset(_store, key);

    if (pubEvent) {
      spartaEventsUtility.pub(
        `${key}-spartaStore`,
        {
          action: 'remove',
          data: { removed, removedItem },
        },
      );
    }

    return removed;
  }

  /**
   * @private
   * Retrieves the state code from session storage (locally) or from a cookie.
   * @returns {string} A two-letter state abbreviation (e.g. "NC").
   */
  _getState() {
    let state = '';

    if (_store.environment === 'LOCAL' && _store.useSessionStorage) {
      const sessionStorageState = this.getPersisted('spartaState', 'sessionStorage');
      if (sessionStorageState) {
        state = sessionStorageState;
      }
    } else {
      // set cookie
      state = spartaGlobalFunctions.spartaGetCookie('state');
    }

    if (typeof state !== 'string' && state.length !== 2) {
      state = '';
    }

    return state;
  }

  /**
   * Returns the state selected by the user using `global/state-selector-module`.
   * @returns {string} A two-letter state abbreviation (e.g. "NC").
   */
  getUserSelectedState() {
    return this._getState();
  }

  /**
   * @private
   * Sets the state code in session storage (locally) or in a cookie.
   * @param {string} state - A two-letter state abbreviation (e.g. "NC").
   * @returns {string|false} The persisted state abbreviation or `false` on failure.
   */
  _setState(state) {
    if (typeof state !== 'string' && state.length !== 2) {
      return false;
    }

    // Use local storage.
    if (_store.environment === 'LOCAL' && _store.useSessionStorage) {
      this.persist('spartaState', state, 'sessionStorage');
    } else {
      // Set a cookie.
      spartaGlobalFunctions.spartaSetCookie('state', state, 365, true);
    }

    return state;
  }
}

export default new SpartaStoreUtility();
